'use client';

/**
 * RFI Widget — Canvas widget with 4 internal views
 *
 * Views: Threads | Matrix | Board | Timeline
 * Receives entityId (project_sid) and default_view from CANVAS_PROPS.
 * Follows SystemObeyaWidget pattern for internal view management.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain rfi
 */

import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  MessageSquare,
  LayoutGrid,
  Columns,
  Clock,
  Loader2,
  XCircle,
  RefreshCw,
  User,
  ChevronDown,
  ChevronRight,
  Paperclip,
  FileText,
  Image,
  ExternalLink,
  Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils/cn';
import { api } from '@/lib/api/client';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RFQComparisonWidget } from './rfq-comparison-widget';
import { TimelineView } from './rfi-timeline-view';
import { PlatformBadge } from '@/components/copilot/details/shared';
import type { RFQCompareResponse } from '@/lib/api/types';

// ── Types (match rfi/queries.py output) ──────────────────────────

interface ThreadMessage {
  cdk: string;
  body: string;
  author_name: string;
  author_id: string | null;
  platform: string;
  message_type: string;
  created_date: string;
  has_attachments: boolean | null;
  attachment_count: number | null;
  attachment_urls: string[] | null;
  attachment_metadata: string | null;
}

interface ThreadRecord {
  rfq_sid: string;
  rfq_name: string;
  rfq_state?: string;   // not returned by current backend; optional for render guards
  message_count: number;
  messages: ThreadMessage[];
}

interface ThreadsResponse {
  threads: ThreadRecord[];
  count: number;
}

// Board is post-Phase-B: one card per tender (x_rfq_group_id) with a
// per-vendor breakdown. Aggregate state is derived across siblings.
interface BoardVendor {
  rfq_sid: string;
  rfq_name: string | null;
  rfq_state: string | null;
  rfq_odoo_id: number | null;
  vendor_name: string | null;
  vendor_sid: string | null;
  response_count: number;
  msg_count: number;
  last_message: string | null;
}

interface BoardCard {
  x_rfq_group_id: string | null;        // null for legacy ungrouped RFQs
  aggregate_state: string;
  created_at: string | null;
  vendor_count: number;
  response_count: number;
  msg_count: number;
  last_message: string | null;
  states: string[];                 // distinct rfq states across siblings
  vendors: BoardVendor[];
}

interface BoardResponse {
  cards: BoardCard[];
  count: number;
}

// ── Attachment helpers ───────────────────────────────────────────

/** Strip HTML tags from Odoo chatter messages into readable plain text. */
function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li[^>]*>/gi, '  • ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function parseAttachments(urls: string[] | null, metadataJson: string | null) {
  if (!urls || urls.length === 0) return [];
  let metadata: Array<{ filename: string; mimetype: string; size: number }> = [];
  if (metadataJson) {
    try { metadata = JSON.parse(metadataJson); } catch { /* fallback */ }
  }
  return urls.map((url, i) => {
    const meta = metadata[i] || { filename: '', mimetype: '', size: 0 };
    return {
      url,
      filename: meta.filename || url.split('/').pop() || 'attachment',
      mimetype: meta.mimetype || 'application/octet-stream',
      size: meta.size || 0,
    };
  });
}

function getFileIcon(mimetype: string) {
  if (mimetype.startsWith('image/')) return Image;
  if (mimetype === 'application/pdf') return FileText;
  return Paperclip;
}

function formatSize(bytes: number): string {
  if (bytes === 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── View Config ──────────────────────────────────────────────────

const VIEWS = [
  { key: 'threads', label: 'Threads', icon: MessageSquare },
  { key: 'matrix', label: 'Matrix', icon: LayoutGrid },
  { key: 'board', label: 'Board', icon: Columns },
  { key: 'timeline', label: 'Messaging', icon: Clock },
] as const;

type ViewKey = (typeof VIEWS)[number]['key'];

// ── Main Widget ──────────────────────────────────────────────────

export function RFIWidget({
  entityId,
  title,
  default_view,
}: {
  entityId?: string;
  title?: string;
  default_view?: string;
}) {
  const [activeView, setActiveView] = useState<ViewKey>(
    (default_view as ViewKey) ?? 'threads'
  );
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);

  // Remember RFQ context so child selections (product, vendor_response)
  // don't clear the matrix or thread filter
  const [contextRfqSid, setContextRfqSid] = useState<string | undefined>(undefined);
  const [contextRfqLabel, setContextRfqLabel] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (selectedNode?.type === 'rfq') {
      setContextRfqSid(selectedNode.id);
      setContextRfqLabel(selectedNode.label);
    }
  }, [selectedNode]);

  const rfqSid = selectedNode?.type === 'rfq' ? selectedNode.id : contextRfqSid;
  const rfqLabel = selectedNode?.type === 'rfq' ? selectedNode.label : contextRfqLabel;

  // Sync from CANVAS_PROPS when matrix selection changes
  useEffect(() => {
    if (
      default_view &&
      ['threads', 'matrix', 'board', 'timeline'].includes(default_view)
    ) {
      setActiveView(default_view as ViewKey);
    }
  }, [default_view]);

  if (!entityId) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Select a project to view RFI conversations</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Internal tab bar */}
      <div className="flex items-center border-b border-border bg-muted/30 px-2">
        {VIEWS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveView(key)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-sm transition-colors',
              activeView === key
                ? 'text-foreground border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* View content */}
      <div className="flex-1 overflow-hidden">
        {activeView === 'threads' && (
          <ThreadsView
            projectSid={entityId}
            selectedRfqSid={rfqSid}
          />
        )}
        {activeView === 'matrix' && (
          <RfiMatrixView
            rfqSid={rfqSid}
            title={rfqLabel ?? title}
          />
        )}
        {activeView === 'board' && <BoardView projectSid={entityId} />}
        {activeView === 'timeline' && (
          <TimelineView projectSid={entityId} selectedRfqSid={rfqSid} />
        )}
      </div>

      {/* Compose moved to copilot rfi_chat tab */}
    </div>
  );
}

// ── Threads View ─────────────────────────────────────────────────

function ThreadsView({ projectSid, selectedRfqSid }: { projectSid: string; selectedRfqSid?: string }) {
  const [threads, setThreads] = useState<ThreadRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchThreads = () => {
    setIsLoading(true);
    setError(null);

    api
      .getStrategyData<ThreadsResponse>('rfi_threads', {
        project_sid: projectSid,
      })
      .then((res) => {
        if (!res.success) {
          setError(res.error || 'Failed to load threads');
          return;
        }
        setThreads(res.data.threads ?? []);
      })
      .catch((err) => setError(err.message ?? 'Network error'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchThreads();
  }, [projectSid]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-10 h-10 mx-auto mb-3 text-destructive" />
          <p className="text-sm text-muted-foreground mb-3">{error}</p>
          <Button size="sm" variant="outline" onClick={fetchThreads}>
            <RefreshCw className="w-3 h-3 mr-1" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Filter to selected RFQ if one is picked in the explorer
  const visible = selectedRfqSid
    ? threads.filter((t) => t.rfq_sid === selectedRfqSid)
    : threads;

  if (visible.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">
            {selectedRfqSid
              ? 'No messages for this RFQ'
              : 'No conversation threads for this project'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto scrollbar-thin">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 border-b border-border/50 bg-background/95 backdrop-blur-sm px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-5 h-5 text-primary" />
            <span className="font-semibold">Conversations</span>
            <Badge variant="secondary" className="text-[10px] px-1.5">
              {visible.length} thread{visible.length !== 1 ? 's' : ''}
            </Badge>
          </div>
          <Button size="sm" variant="ghost" onClick={fetchThreads} className="h-7 w-7 p-0">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {visible.map((thread) => (
          <RFQThread key={thread.rfq_sid} thread={thread} />
        ))}
      </div>
    </div>
  );
}

function RFQThread({ thread }: { thread: ThreadRecord }) {
  const [expanded, setExpanded] = useState(thread.message_count <= 10);

  const stateColor: Record<string, string> = {
    draft: 'text-muted-foreground border-muted-foreground/50',
    sent: 'text-info border-info/50',
    purchase: 'text-warning border-warning/50',
    done: 'text-success border-success/50',
  };

  return (
    <div className="glass-card rounded-xl overflow-hidden group hover:border-primary/30 transition-all duration-300">
      {/* Thread header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-accent/30 transition-colors text-left"
      >
        <div className="p-1.5 rounded-lg bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
          {expanded
            ? <ChevronDown className="w-4 h-4" />
            : <ChevronRight className="w-4 h-4" />}
        </div>
        <span className="font-semibold text-sm flex-1 truncate">
          {thread.rfq_name}
        </span>
        <Badge variant="secondary" className="text-[10px] px-1.5">
          {thread.message_count} msg
        </Badge>
        {thread.rfq_state && (
          <Badge
            variant="outline"
            className={cn('text-[10px]', stateColor[thread.rfq_state])}
          >
            {thread.rfq_state}
          </Badge>
        )}
      </button>

      {/* Messages */}
      {expanded && thread.messages && (
        <div className="border-t border-border/30 divide-y divide-border/20">
          {thread.messages
            .filter((m) => m.body)
            .map((msg, i) => (
              <MessageRow key={msg.cdk || i} message={msg} />
            ))}
          {thread.messages.filter((m) => m.body).length === 0 && (
            <div className="px-5 py-4 text-xs text-muted-foreground text-center">
              No message content (system notifications only)
            </div>
          )}
        </div>
      )}

      {!expanded && thread.message_count > 0 && (
        <button
          onClick={() => setExpanded(true)}
          className="w-full px-5 py-2.5 text-xs text-primary hover:bg-accent/30 transition-colors border-t border-border/30"
        >
          Show {thread.message_count} messages
        </button>
      )}
    </div>
  );
}

function MessageRow({ message }: { message: ThreadMessage }) {
  const initial = (message.author_name || '?')[0].toUpperCase();
  const timestamp = message.created_date
    ? new Date(message.created_date).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  const attachments = parseAttachments(message.attachment_urls, message.attachment_metadata);

  return (
    <div className="flex gap-3 px-5 py-3 hover:bg-accent/30 transition-colors">
      {/* Avatar */}
      <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 text-xs font-semibold">
        {initial}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-semibold truncate">
            {message.author_name || 'Unknown'}
          </span>
          {message.platform && <PlatformBadge platform={message.platform} />}
          <span className="text-[10px] text-muted-foreground ml-auto shrink-0">
            {timestamp}
          </span>
        </div>
        <p className="text-sm text-foreground/80 whitespace-pre-wrap break-words leading-relaxed">
          {stripHtml(message.body)}
        </p>
        {attachments.length > 0 && (
          <div className="mt-2 space-y-1">
            {attachments.map((att, i) => {
              const Icon = getFileIcon(att.mimetype);
              return (
                <a
                  key={i}
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs text-primary hover:underline bg-primary/5 border border-primary/20 rounded-lg px-2.5 py-1.5 transition-colors hover:bg-primary/10"
                >
                  <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="truncate max-w-[200px]">{att.filename}</span>
                  {att.size > 0 && (
                    <span className="text-muted-foreground flex-shrink-0">{formatSize(att.size)}</span>
                  )}
                  <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// PlatformBadge imported from '@/components/copilot/details/shared'

// ── Matrix View (BFF) ────────────────────────────────────────────

function RfiMatrixView({ rfqSid, title }: { rfqSid?: string; title?: string }) {
  const comparisonStrategy = useCapabilityStore((s) => s.strategyName('rfi', 'comparison'));
  const { data, isLoading } = useStrategyData<RFQCompareResponse>(
    comparisonStrategy ?? 'rfi_comparison',
    { rfq_sid: rfqSid ?? '' },
    !!rfqSid,
  );

  if (!rfqSid) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
        <div className="p-4 rounded-2xl bg-muted/30 mb-4">
          <LayoutGrid className="w-10 h-10 opacity-40" />
        </div>
        <p className="text-sm font-medium">No RFQ selected</p>
        <p className="text-xs mt-1">Select an RFQ from the explorer to see the comparison matrix</p>
      </div>
    );
  }

  const compareData = (data?.data ?? null) as RFQCompareResponse | null;

  return (
    <RFQComparisonWidget
      entityId={rfqSid}
      title={title}
      externalData={compareData}
      externalLoading={isLoading}
    />
  );
}

// ── Board View ───────────────────────────────────────────────────

const BOARD_COLUMNS = [
  { key: 'draft', label: 'Draft', color: 'text-muted-foreground', dot: 'bg-muted-foreground' },
  { key: 'sent', label: 'Awaiting Quote', color: 'text-info', dot: 'bg-info' },
  { key: 'purchase', label: 'In Progress', color: 'text-warning', dot: 'bg-warning' },
  { key: 'done', label: 'Resolved', color: 'text-success', dot: 'bg-success' },
  { key: 'cancel', label: 'Cancelled', color: 'text-destructive', dot: 'bg-destructive' },
  { key: 'mixed', label: 'Mixed', color: 'text-muted-foreground', dot: 'bg-muted-foreground/50' },
] as const;

function BoardView({ projectSid }: { projectSid: string }) {
  const [cards, setCards] = useState<BoardCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingKey, setCancellingKey] = useState<string | null>(null);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);
  const queryClient = useQueryClient();

  const fetchBoard = () => {
    setIsLoading(true);
    setError(null);

    api
      .getStrategyData<BoardResponse>('rfi_board', {
        project_sid: projectSid,
      })
      .then((res) => {
        if (!res.success) {
          setError(res.error || 'Failed to load board');
          return;
        }
        setCards(res.data.cards ?? []);
      })
      .catch((err) => setError(err.message ?? 'Network error'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchBoard();
  }, [projectSid]);

  /**
   * Cancel a tender from the board. Fires rfq_delete on the anchor sibling
   * with apply_to_group=true when the card represents a multi-vendor tender,
   * so all siblings transition to state='cancel' and each invited vendor
   * gets the cancellation chatter + notification per the unified messaging
   * path. Card click is suppressed via stopPropagation on the trigger.
   */
  const handleCancelCard = useCallback(
    async (card: BoardCard, cardKey: string) => {
      const anchor = card.vendors[0];
      if (!anchor?.rfq_odoo_id) return;

      const isGroup = !!card.x_rfq_group_id && card.vendor_count > 1;
      const confirmMsg = isGroup
        ? `Cancel this tender? ${card.vendor_count} vendors will be notified.`
        : 'Cancel this RFQ? The vendor will be notified.';
      if (!confirm(confirmMsg)) return;

      setCancellingKey(cardKey);
      try {
        const result = await api.getStrategyData<{ message?: string }>(
          'rfq_delete',
          {
            rfq_id: anchor.rfq_odoo_id,
            apply_to_group: isGroup,
            cancellation_reason: 'cancelled_from_board',
          },
        );
        if (result.success) {
          toast.success(result.data?.message || 'Cancelled');
          fetchBoard();
          queryClient.invalidateQueries({ queryKey: ['strategy-data', 'rfi_details'] });
          queryClient.invalidateQueries({ queryKey: ['rfq-comparison'] });
          queryClient.invalidateQueries({ queryKey: ['obeya-projects'] });
        } else {
          toast.error(result.error || 'Cancel failed');
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Cancel failed');
      } finally {
        setCancellingKey(null);
      }
    },
    [queryClient],
  );

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-10 h-10 mx-auto mb-3 text-destructive" />
          <p className="text-sm text-muted-foreground mb-3">{error}</p>
          <Button size="sm" variant="outline" onClick={fetchBoard}>
            <RefreshCw className="w-3 h-3 mr-1" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Group cards by aggregate state (one card = one tender / x_rfq_group_id)
  const columns = BOARD_COLUMNS.map((col) => ({
    ...col,
    cards: cards.filter((c) => c.aggregate_state === col.key),
  }));

  return (
    <div className="h-full flex flex-col">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 border-b border-border/50 bg-background/95 backdrop-blur-sm px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Columns className="w-5 h-5 text-primary" />
            <span className="font-semibold">Pipeline</span>
            <Badge variant="secondary" className="text-[10px] px-1.5">
              {cards.length} tender{cards.length !== 1 ? 's' : ''}
            </Badge>
          </div>
          <Button size="sm" variant="ghost" onClick={fetchBoard} className="h-7 w-7 p-0">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Kanban columns */}
      <div className="flex-1 overflow-auto scrollbar-thin">
        <div className="flex gap-3 p-4 h-full">
          {columns.map((col) => (
            <div key={col.key} className="flex-1 min-w-[220px] flex flex-col bg-muted/30 rounded-xl p-3">
              {/* Column Header */}
              <div className="flex items-center gap-2.5 mb-3 px-1">
                <span className={cn('w-2.5 h-2.5 rounded-full', col.dot)} />
                <span className={cn('text-sm font-semibold', col.color)}>
                  {col.label}
                </span>
                <Badge variant="secondary" className="text-[10px] px-1.5 ml-auto">
                  {col.cards.length}
                </Badge>
              </div>

              {/* Cards (one per tender / x_rfq_group_id) */}
              <div className="space-y-3 flex-1">
                {col.cards.map((card) => {
                  // Select the first sibling as the representative node —
                  // tender-level selection isn't graph-addressable yet.
                  const anchor = card.vendors[0];
                  const cardKey = card.x_rfq_group_id ?? anchor?.rfq_sid ?? 'legacy';
                  const tenderLabel = card.x_rfq_group_id ?? (anchor?.rfq_name ?? 'Ungrouped RFQ');
                  const isCancellable =
                    card.aggregate_state !== 'cancel' && card.aggregate_state !== 'done';
                  const isCancellingThis = cancellingKey === cardKey;

                  return (
                    <button
                      key={cardKey}
                      onClick={() => {
                        if (anchor?.rfq_sid) {
                          selectNode({
                            id: anchor.rfq_sid,
                            label: anchor.rfq_name || tenderLabel,
                            type: 'rfq',
                            status: anchor.rfq_state || undefined,
                            properties: { project_sid: projectSid, x_rfq_group_id: card.x_rfq_group_id },
                          }, projectSid);
                        }
                      }}
                      className={cn(
                        'w-full text-left glass-card rounded-xl p-4 group relative',
                        'hover:border-primary/30 hover:shadow-sm transition-all duration-300',
                        anchor && selectedNode?.id === anchor.rfq_sid && 'border-primary bg-primary/5 ring-1 ring-primary/20'
                      )}
                    >
                      {/* Cancel — appears on hover only for live tenders;
                          stopPropagation so click doesn't select the card. */}
                      {isCancellable && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleCancelCard(card, cardKey);
                          }}
                          disabled={isCancellingThis}
                          title={card.vendor_count > 1 && card.x_rfq_group_id
                            ? `Cancel tender (${card.vendor_count} vendors)`
                            : 'Cancel RFQ'}
                          className={cn(
                            'absolute top-2 right-2 p-1 rounded text-muted-foreground',
                            'opacity-0 group-hover:opacity-100 transition-opacity',
                            'hover:text-destructive hover:bg-destructive/10',
                            'disabled:opacity-50 disabled:cursor-wait',
                          )}
                        >
                          {isCancellingThis
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Ban className="w-3.5 h-3.5" />
                          }
                        </button>
                      )}

                      <div className="flex items-center gap-2 mb-2">
                        <div className="font-semibold text-sm truncate flex-1">
                          {tenderLabel}
                        </div>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          {card.vendor_count} vendor{card.vendor_count !== 1 ? 's' : ''}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground mb-3">
                        {card.vendors.slice(0, 3).map((v) => (
                          <span
                            key={v.rfq_sid}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted/50 max-w-[120px]"
                          >
                            <User className="w-3 h-3 shrink-0" />
                            <span className="truncate">{v.vendor_name || 'No vendor'}</span>
                          </span>
                        ))}
                        {card.vendors.length > 3 && (
                          <span className="text-[10px] text-muted-foreground">
                            +{card.vendors.length - 3}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted/50">
                          {card.response_count} response{card.response_count !== 1 ? 's' : ''}
                        </span>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted/50">
                          <MessageSquare className="w-3 h-3" />
                          {card.msg_count}
                        </span>
                      </div>

                      <div className="text-[10px] text-muted-foreground mt-3 pt-2 border-t border-border/30">
                        {card.last_message
                          ? <>Last activity: {new Date(card.last_message).toLocaleDateString(undefined, {
                              month: 'short', day: 'numeric',
                            })}</>
                          : <span className="text-muted-foreground/40">No activity</span>
                        }
                      </div>
                    </button>
                  );
                })}

                {col.cards.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-8 border border-dashed border-border/50 rounded-xl text-muted-foreground">
                    <Columns className="w-6 h-6 mb-2 opacity-30" />
                    <span className="text-xs">No tenders</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// TimelineView extracted to rfi-timeline-view.tsx
// Compose drawer moved to copilot rfi-chat-panel.tsx
